(function headerControllerFactory(root) {
  'use strict';

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const HEADER_AWARE_STICKY_SELECTOR = [
    '.shipping-process__header',
    '.shipping-policies__heading',
    '.store-credit-steps__header',
    '.policy-row__header',
    '.faq-row__header',
    '.home-aquarium-guide__intro',
    '.article-detail__sidebar',
    '.collection-page__sidebar',
  ].join(',');

  const headerAwareStickyAnimations = new Map();

  function cssDurationToMilliseconds(value) {
    const normalizedValue = String(value || '').trim();
    const numericValue = Number.parseFloat(normalizedValue);

    if (!Number.isFinite(numericValue)) {
      return 0;
    }

    return normalizedValue.endsWith('s') && !normalizedValue.endsWith('ms')
      ? numericValue * 1000
      : numericValue;
  }

  function isVisibleFocusable(element) {
    if (!element || element.closest?.('[inert], [aria-hidden="true"]')) {
      return false;
    }

    if (typeof element.getClientRects === 'function' && element.getClientRects().length === 0) {
      return false;
    }

    const style = typeof root.getComputedStyle === 'function'
      ? root.getComputedStyle(element)
      : null;

    return !style || (style.display !== 'none' && style.visibility !== 'hidden');
  }

  function getFocusableElements(container) {
    if (!container || typeof container.querySelectorAll !== 'function') {
      return [];
    }

    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(isVisibleFocusable);
  }

  function closestFocusTarget(target) {
    if (!target || typeof target.closest !== 'function') {
      return null;
    }

    return target.closest(FOCUSABLE_SELECTOR);
  }

  function registerAlpine() {
    if (!root.Alpine || root.__jftHeaderControllerRegistered) {
      return;
    }

    root.__jftHeaderControllerRegistered = true;

    root.Alpine.data('siteHeader', () => ({
      drawerOpen: false,
      searchDrawerOpen: false,
      cartDrawerOpen: false,
      activeMenu: null,
      activeSubmenu: null,
      desktopMenuPinned: null,
      desktopMenuCloseTimer: null,
      desktopSubmenuCloseTimer: null,
      mobilePanel: 'root',
      lastMobilePanel: null,
      mobilePanelIndex: 0,
      isDesktop: root.matchMedia('(min-width: 1024px)').matches,
      canHoverHeader: root.matchMedia('(hover: hover) and (pointer: fine)').matches,
      headerVisible: true,
      lastScrollY: Math.max(root.scrollY || 0, 0),
      pendingScrollY: Math.max(root.scrollY || 0, 0),
      scrollAccumulator: 0,
      scrollDirection: 0,
      headerHeight: root.matchMedia('(min-width: 1024px)').matches ? 144 : 96,
      headerOriginBoundary: root.matchMedia('(min-width: 1024px)').matches ? 144 : 96,
      headerHideTimer: null,
      headerScrollFrame: null,
      headerPointerFrame: null,
      pendingPointerY: Number.POSITIVE_INFINITY,
      pendingPointerTarget: null,
      headerInteractionLocked: false,
      headerInteractionTimer: null,
      headerRevealSettled: true,
      headerPointerDown: false,
      headerHoverArmed: true,
      keyboardNavigationActive: false,
      externalModalMotionLocked: false,
      externalModalMotionTimer: null,
      mobileTrigger: null,
      mobilePanelTrigger: null,
      searchTrigger: null,
      restoreMobileFocus: true,
      restoreSearchFocus: true,

      init() {
        this.$nextTick(() => this.measureHeader());

        this.$watch('drawerOpen', (isOpen) => {
          if (isOpen && !this.isDesktop) {
            this.showHeader();
            this.$nextTick(() => this.focusMobileDialog());
          } else if (!isOpen) {
            const trigger = this.mobileTrigger;
            const shouldRestore = this.restoreMobileFocus;
            this.mobileTrigger = null;
            this.mobilePanelTrigger = null;
            this.restoreMobileFocus = true;
            this.mobilePanel = 'root';
            this.lastMobilePanel = null;
            this.mobilePanelIndex = 0;

            if (shouldRestore) {
              this.$nextTick(() => this.restoreFocus(trigger));
            }
          }
        });

        this.$watch('searchDrawerOpen', (isOpen) => {
          if (isOpen) {
            this.showHeader();
            this.$nextTick(() => this.$dispatch('header-search:focus'));
          } else {
            const trigger = this.searchTrigger;
            const shouldRestore = this.restoreSearchFocus;
            this.searchTrigger = null;
            this.restoreSearchFocus = true;

            if (shouldRestore) {
              this.$nextTick(() => this.restoreFocus(trigger));
            }
          }
        });
      },

      destroy() {
        this.clearHeaderHideTimer();
        this.clearHeaderInteractionTimer();
        this.clearDesktopMenuCloseTimer();
        this.clearExternalModalMotionTimer();
        this.cancelHeaderScrollFrame();
        this.cancelHeaderPointerFrame();
        headerAwareStickyAnimations.forEach((animation) => animation.cancel());
        headerAwareStickyAnimations.clear();
        this.releaseManagedInert();
        root.document?.documentElement?.classList.remove(
          'is-header-drawer-open',
          'is-search-drawer-open',
          'is-site-header-hidden',
        );
        this.$root?.classList?.remove?.(
          'site-header--motion-instant',
          'site-header--origin-reveal',
        );
        root.document?.documentElement?.style?.removeProperty?.('--site-header-height');
      },

      restoreFocus(target) {
        if (target?.isConnected && !target.closest?.('[inert]')) {
          target.focus({ preventScroll: true });
        }
      },

      handleResize() {
        this.isDesktop = root.matchMedia('(min-width: 1024px)').matches;
        this.canHoverHeader = root.matchMedia('(hover: hover) and (pointer: fine)').matches;
        this.closeDesktopMenu();
        this.showHeader(false);
        this.lastScrollY = Math.max(root.scrollY || 0, 0);
        this.pendingScrollY = this.lastScrollY;
        this.scrollAccumulator = 0;
        this.scrollDirection = 0;
        this.headerHoverArmed = true;
        this.$nextTick(() => this.measureHeader());

        if (this.isDesktop && this.drawerOpen) {
          this.closeMobileMenu(false);
        }
      },

      clearHeaderHideTimer() {
        if (this.headerHideTimer !== null) {
          root.clearTimeout(this.headerHideTimer);
          this.headerHideTimer = null;
        }
      },

      clearHeaderInteractionTimer() {
        if (this.headerInteractionTimer !== null) {
          root.clearTimeout(this.headerInteractionTimer);
          this.headerInteractionTimer = null;
        }
      },

      clearDesktopMenuCloseTimer() {
        if (this.desktopMenuCloseTimer !== null) {
          root.clearTimeout(this.desktopMenuCloseTimer);
          this.desktopMenuCloseTimer = null;
        }
      },

      clearExternalModalMotionTimer() {
        if (this.externalModalMotionTimer !== null) {
          root.clearTimeout(this.externalModalMotionTimer);
          this.externalModalMotionTimer = null;
        }
      },

      lockHeaderInteractions() {
        this.clearHeaderInteractionTimer();
        this.headerInteractionLocked = true;
        this.headerRevealSettled = false;
        this.headerHoverArmed = false;

        const reducedMotion = root.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const motionStyle = typeof root.getComputedStyle === 'function'
          ? root.getComputedStyle(root.document?.documentElement)
          : null;
        const fastDuration = cssDurationToMilliseconds(
          motionStyle?.getPropertyValue('--motion-duration-fast'),
        );
        const settleDelay = reducedMotion ? 0 : fastDuration + 20;
        this.headerInteractionTimer = root.setTimeout(() => {
          this.headerInteractionTimer = null;
          this.$root?.classList?.remove?.('site-header--origin-reveal');
          this.headerRevealSettled = true;
          this.releaseHeaderInteractions();
        }, settleDelay);
      },

      releaseHeaderInteractions(delay = 0) {
        if (!this.headerInteractionLocked || !this.headerRevealSettled || this.headerPointerDown) {
          return;
        }

        this.clearHeaderInteractionTimer();
        this.headerInteractionTimer = root.setTimeout(() => {
          this.headerInteractionTimer = null;
          if (this.headerRevealSettled && !this.headerPointerDown) {
            this.headerInteractionLocked = false;
            this.headerHoverArmed = true;
          }
        }, delay);
      },

      handleGlobalPointerDown() {
        this.headerPointerDown = true;
        this.keyboardNavigationActive = false;
      },

      handleGlobalPointerEnd() {
        this.headerPointerDown = false;
        this.releaseHeaderInteractions(80);
      },

      handleGlobalKeyDown(event) {
        if (event.key === 'Tab') {
          this.keyboardNavigationActive = true;
        }
      },

      guardHeaderInteraction(event) {
        if (!this.headerInteractionLocked || event.detail === 0) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
      },

      cancelHeaderScrollFrame() {
        if (this.headerScrollFrame === null) {
          return;
        }

        if (typeof root.cancelAnimationFrame === 'function') {
          root.cancelAnimationFrame(this.headerScrollFrame);
        } else {
          root.clearTimeout(this.headerScrollFrame);
        }

        this.headerScrollFrame = null;
      },

      cancelHeaderPointerFrame() {
        if (this.headerPointerFrame === null) {
          return;
        }

        if (typeof root.cancelAnimationFrame === 'function') {
          root.cancelAnimationFrame(this.headerPointerFrame);
        } else {
          root.clearTimeout(this.headerPointerFrame);
        }

        this.headerPointerFrame = null;
      },

      disableHeaderMotionForFrame() {
        const headerRoot = this.$root;
        if (!headerRoot?.classList) {
          return;
        }

        headerRoot.classList.add('site-header--motion-instant');

        const restoreHeaderMotion = () => {
          headerRoot.classList.remove('site-header--motion-instant');
        };

        if (typeof root.requestAnimationFrame === 'function') {
          root.requestAnimationFrame(restoreHeaderMotion);
        } else {
          root.setTimeout(restoreHeaderMotion, 16);
        }
      },

      measureHeader() {
        this.headerHeight = this.$root?.offsetHeight || (this.isDesktop ? 144 : 96);
        const mainContent = root.document?.getElementById?.('MainContent');
        const mainContentTop = mainContent?.getBoundingClientRect
          ? mainContent.getBoundingClientRect().top + Math.max(root.scrollY || 0, 0)
          : this.headerHeight;
        this.headerOriginBoundary = Math.max(this.headerHeight, mainContentTop);
        this.syncHeaderAwareStickyState(false);
      },

      syncHeaderAwareStickyState(animate = true) {
        const documentRoot = root.document?.documentElement;
        if (!documentRoot) {
          return;
        }

        documentRoot?.style?.setProperty?.('--site-header-height', `${this.headerHeight}px`);
        const shouldHideHeader = !this.headerVisible;
        const headerStateChanged = documentRoot.classList?.contains?.('is-site-header-hidden') !== shouldHideHeader;

        if (!headerStateChanged) {
          return;
        }

        const reducedMotion = root.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const canAnimate = animate
          && !reducedMotion
          && typeof root.getComputedStyle === 'function'
          && typeof root.document?.querySelectorAll === 'function';
        const stickyEntries = canAnimate
          ? Array.from(root.document.querySelectorAll(HEADER_AWARE_STICKY_SELECTOR))
            .filter((element) => root.getComputedStyle(element).position === 'sticky')
            .map((element) => ({ element, previousTop: element.getBoundingClientRect().top }))
          : [];

        stickyEntries.forEach(({ element }) => {
          headerAwareStickyAnimations.get(element)?.cancel();
          headerAwareStickyAnimations.delete(element);
        });

        this.$root?.classList?.toggle?.('site-header--hidden', shouldHideHeader);
        documentRoot.classList?.toggle?.('is-site-header-hidden', shouldHideHeader);

        if (stickyEntries.length === 0) {
          return;
        }

        const rootStyle = root.getComputedStyle(documentRoot);
        const duration = cssDurationToMilliseconds(
          rootStyle.getPropertyValue('--motion-duration-fast'),
        );
        const easing = rootStyle.getPropertyValue('--motion-ease-standard').trim() || 'ease';

        if (duration === 0) {
          return;
        }

        stickyEntries.forEach(({ element, previousTop }) => {
          const currentTop = element.getBoundingClientRect().top;
          const offsetDelta = previousTop - currentTop;

          if (Math.abs(offsetDelta) < 0.5 || typeof element.animate !== 'function') {
            return;
          }

          const animation = element.animate(
            [
              { transform: `translate3d(0, ${offsetDelta}px, 0)` },
              { transform: 'translate3d(0, 0, 0)' },
            ],
            { duration, easing },
          );
          const releaseAnimation = () => {
            if (headerAwareStickyAnimations.get(element) === animation) {
              headerAwareStickyAnimations.delete(element);
            }
          };

          animation.onfinish = releaseAnimation;
          animation.oncancel = releaseAnimation;
          headerAwareStickyAnimations.set(element, animation);
        });
      },

      showHeader(animate = true) {
        this.clearHeaderHideTimer();
        this.clearHeaderInteractionTimer();
        this.$root?.classList?.remove?.('site-header--origin-reveal');
        if (!animate) {
          this.disableHeaderMotionForFrame();
        }
        this.headerRevealSettled = true;
        this.headerInteractionLocked = false;
        this.headerVisible = true;
        this.syncHeaderAwareStickyState(animate);
      },

      handleHeaderEnter() {
        this.clearHeaderHideTimer();
        this.revealHeader();
      },

      revealHeader() {
        this.clearHeaderHideTimer();
        if (!this.headerVisible) {
          this.lockHeaderInteractions();
          this.headerVisible = true;
        }
        this.syncHeaderAwareStickyState();
      },

      revealHeaderAtOrigin() {
        this.clearHeaderHideTimer();
        if (this.headerVisible) {
          this.syncHeaderAwareStickyState();
          return;
        }

        this.$root?.classList?.add?.('site-header--origin-reveal');
        this.revealHeader();
      },

      openDesktopMenu(menuKey) {
        if (!this.isDesktop || this.headerInteractionLocked || !this.headerHoverArmed) {
          return;
        }

        this.clearDesktopMenuCloseTimer();
        if (this.desktopMenuPinned !== null && this.desktopMenuPinned !== menuKey) {
          this.desktopMenuPinned = null;
        }

        this.activeMenu = menuKey;
      },

      // The third level used to be a pure CSS :hover flyout. Crossing the gap to
      // reach it dropped :hover, and the close already scheduled by the parent's
      // mouseleave was never cancelled once the pointer came to rest, so the menu
      // disappeared underneath the cursor. It now uses the same state and grace
      // period as the level above it, which does not depend on an unbroken hover
      // chain across two boxes.
      openDesktopSubmenu(submenuKey, menuKey) {
        if (!this.isDesktop) {
          return;
        }

        this.clearDesktopSubmenuCloseTimer();

        if (menuKey) {
          this.openDesktopMenu(menuKey);
        }

        this.activeSubmenu = submenuKey;
      },

      scheduleDesktopSubmenuClose(submenuKey) {
        if (this.desktopSubmenuCloseTimer !== null) {
          return;
        }

        this.desktopSubmenuCloseTimer = root.setTimeout(() => {
          this.desktopSubmenuCloseTimer = null;
          if (this.activeSubmenu === submenuKey) {
            this.activeSubmenu = null;
          }
        }, 200);
      },

      clearDesktopSubmenuCloseTimer() {
        if (this.desktopSubmenuCloseTimer !== null) {
          root.clearTimeout(this.desktopSubmenuCloseTimer);
          this.desktopSubmenuCloseTimer = null;
        }
      },

      toggleDesktopMenu(menuKey) {
        this.clearDesktopMenuCloseTimer();
        if (this.activeMenu === menuKey && this.desktopMenuPinned === menuKey) {
          this.closeDesktopMenu(menuKey);
          return;
        }

        this.activeMenu = menuKey;
        this.desktopMenuPinned = menuKey;
      },

      scheduleDesktopMenuClose(menuKey) {
        if (this.desktopMenuPinned === menuKey || this.desktopMenuCloseTimer !== null) {
          return;
        }

        this.desktopMenuCloseTimer = root.setTimeout(() => {
          this.desktopMenuCloseTimer = null;
          this.closeDesktopMenu(menuKey);
        }, 120);
      },

      closeDesktopMenu(menuKey = null) {
        if (
          menuKey !== null
          && this.activeMenu !== menuKey
          && this.desktopMenuPinned !== menuKey
        ) {
          return;
        }

        this.clearDesktopMenuCloseTimer();
        this.clearDesktopSubmenuCloseTimer();
        this.activeMenu = null;
        this.activeSubmenu = null;
        this.desktopMenuPinned = null;
      },

      shouldKeepHeaderVisible() {
        const activeElement = root.document?.activeElement;
        return this.drawerOpen
          || this.searchDrawerOpen
          || this.cartDrawerOpen
          || this.activeMenu !== null
          || Boolean(
            this.keyboardNavigationActive
              && activeElement
              && this.$root?.contains(activeElement),
          );
      },

      isHeaderMotionSuspended() {
        const documentClassList = root.document?.documentElement?.classList;
        if (this.externalModalMotionLocked) {
          return true;
        }

        return Boolean(documentClassList) && [
          'product-preview-modal-open',
          'image-lightbox-open',
          'is-collection-filter-open',
          'is-search-filter-open',
        ].some((className) => documentClassList.contains(className));
      },

      handleExternalModalOpen() {
        this.clearExternalModalMotionTimer();
        this.externalModalMotionLocked = true;
        this.closeHeaderOverlays(false);
        this.clearHeaderHideTimer();
        this.scrollAccumulator = 0;
        this.scrollDirection = 0;
        this.lastScrollY = Math.max(root.scrollY || 0, 0);
      },

      handleExternalModalClose() {
        this.clearExternalModalMotionTimer();
        this.externalModalMotionLocked = true;
        this.externalModalMotionTimer = root.setTimeout(() => {
          this.externalModalMotionTimer = null;
          this.lastScrollY = Math.max(root.scrollY || 0, 0);
          this.scrollAccumulator = 0;
          this.scrollDirection = 0;
          this.externalModalMotionLocked = false;
        }, 450);
      },

      hideHeader() {
        if (this.shouldKeepHeaderVisible()) {
          this.showHeader();
          return;
        }

        this.clearHeaderHideTimer();
        this.clearHeaderInteractionTimer();
        this.$root?.classList?.remove?.('site-header--origin-reveal');
        this.headerInteractionLocked = true;
        this.headerRevealSettled = true;
        this.headerHoverArmed = false;
        this.headerVisible = false;
        this.syncHeaderAwareStickyState();
      },

      handleHeaderScroll() {
        this.pendingScrollY = Math.max(root.scrollY || root.document?.documentElement?.scrollTop || 0, 0);

        if (this.headerScrollFrame !== null) {
          return;
        }

        const processScroll = () => {
          this.headerScrollFrame = null;
          this.processHeaderScroll(this.pendingScrollY);
        };

        if (typeof root.requestAnimationFrame === 'function') {
          this.headerScrollFrame = root.requestAnimationFrame(processScroll);
        } else {
          this.headerScrollFrame = root.setTimeout(processScroll, 16);
        }
      },

      processHeaderScroll(currentScrollY) {
        const delta = currentScrollY - this.lastScrollY;
        const direction = Math.sign(delta);
        this.lastScrollY = currentScrollY;

        if (this.isHeaderMotionSuspended()) {
          this.scrollAccumulator = 0;
          this.scrollDirection = 0;
          return;
        }

        if (currentScrollY <= this.headerOriginBoundary || this.shouldKeepHeaderVisible()) {
          this.scrollAccumulator = 0;
          this.scrollDirection = 0;
          if (currentScrollY <= this.headerOriginBoundary) {
            this.revealHeaderAtOrigin();
          } else {
            this.revealHeader();
          }
          return;
        }

        if (direction === 0) {
          return;
        }

        if (direction !== this.scrollDirection) {
          this.scrollAccumulator = delta;
          this.scrollDirection = direction;
        } else {
          this.scrollAccumulator += delta;
        }

        if (this.scrollAccumulator <= -12) {
          if (!this.isDesktop || !this.canHoverHeader) {
            this.revealHeader();
          }
          this.scrollAccumulator = 0;
          return;
        }

        if (this.scrollAccumulator >= 24 && currentScrollY > this.headerOriginBoundary) {
          this.hideHeader();
          this.scrollAccumulator = 0;
        }
      },

      handlePointerMove(event) {
        if (!this.isDesktop || !this.canHoverHeader) {
          return;
        }

        this.pendingPointerY = event.clientY;
        this.pendingPointerTarget = event.target || null;

        if (this.headerPointerFrame !== null) {
          return;
        }

        const processPointerMove = () => {
          this.headerPointerFrame = null;
          this.processHeaderPointerMove(this.pendingPointerY, this.pendingPointerTarget);
        };

        if (typeof root.requestAnimationFrame === 'function') {
          this.headerPointerFrame = root.requestAnimationFrame(processPointerMove);
        } else {
          this.headerPointerFrame = root.setTimeout(processPointerMove, 16);
        }
      },

      processHeaderPointerMove(pointerY, pointerTarget) {
        if (
          !this.isDesktop
          || !this.canHoverHeader
          || this.lastScrollY <= this.headerOriginBoundary
          || this.isHeaderMotionSuspended()
        ) {
          return;
        }

        if (
          this.activeMenu !== null
          && pointerTarget
          && !this.$root?.contains(pointerTarget)
        ) {
          this.closeDesktopMenu();
        }

        if (!this.headerVisible && pointerY <= 16) {
          this.revealHeader();
          return;
        }

        if (this.headerVisible && pointerY <= this.headerHeight) {
          this.clearHeaderHideTimer();
          if (!this.headerInteractionLocked) {
            this.headerHoverArmed = true;
          }
          return;
        }

        if (this.headerVisible) {
          this.handleHeaderLeave();
        }
      },

      handleHeaderLeave() {
        if (
          !this.isDesktop
          || !this.canHoverHeader
          || this.lastScrollY <= this.headerOriginBoundary
          || this.shouldKeepHeaderVisible()
          || this.isHeaderMotionSuspended()
        ) {
          return;
        }

        if (this.headerHideTimer !== null) {
          return;
        }

        this.headerHideTimer = root.setTimeout(() => this.hideHeader(), 180);
      },

      handleHeaderFocusOut() {
        this.$nextTick(() => {
          if (!this.$root?.contains(root.document?.activeElement)) {
            this.keyboardNavigationActive = false;
            this.handleHeaderLeave();
          }
        });
      },

      handleEscape(event) {
        if (this.searchDrawerOpen) {
          event.preventDefault();
          this.closeSearchDrawer();
          return;
        }

        if (this.drawerOpen && !this.isDesktop) {
          event.preventDefault();
          this.closeMobileMenu();
          return;
        }

        this.closeDesktopMenu();
      },

      openSearchDrawer(event) {
        if (!this.searchDrawerOpen) {
          this.searchTrigger = closestFocusTarget(event?.target) || root.document?.activeElement;
        }

        this.closeMobileMenu(false);
        this.searchDrawerOpen = true;
        this.closeDesktopMenu();
        this.mobilePanel = 'root';
      },

      closeSearchDrawer(restoreFocus = true) {
        this.restoreSearchFocus = restoreFocus;
        this.searchDrawerOpen = false;
      },

      toggleMobileMenu(trigger) {
        if (this.drawerOpen) {
          this.closeMobileMenu();
          return;
        }

        this.mobileTrigger = closestFocusTarget(trigger) || trigger || root.document?.activeElement;
        this.closeSearchDrawer(false);
        this.drawerOpen = true;
        this.closeDesktopMenu();
        this.mobilePanel = 'root';
        this.lastMobilePanel = null;
      },

      closeMobileMenu(restoreFocus = true) {
        this.restoreMobileFocus = restoreFocus;
        this.drawerOpen = false;
        this.closeDesktopMenu();
        this.mobilePanel = 'root';
        this.lastMobilePanel = null;
      },

      closeHeaderOverlays(restoreFocus = false) {
        this.closeSearchDrawer(restoreFocus);
        this.closeMobileMenu(restoreFocus);
        this.closeDesktopMenu();
      },

      openMobilePanel(panel, trigger) {
        this.mobilePanelTrigger = closestFocusTarget(trigger) || trigger || root.document?.activeElement;
        this.mobilePanel = panel;
        this.lastMobilePanel = panel;
        this.mobilePanelIndex = 1;
        this.$nextTick(() => this.focusMobileDialog());
      },

      closeMobilePanel() {
        const trigger = this.mobilePanelTrigger;
        this.mobilePanel = 'root';
        this.mobilePanelIndex = 0;
        this.mobilePanelTrigger = null;
        this.$nextTick(() => this.restoreFocus(trigger));
      },

      focusMobileDialog() {
        const dialog = this.$root?.querySelector('.site-header__mobile-menu');
        const focusable = getFocusableElements(dialog);
        (focusable[0] || dialog)?.focus?.({ preventScroll: true });
      },

      trapFocus(event, container) {
        if (event.key !== 'Tab') {
          return;
        }

        const focusable = getFocusableElements(container);
        if (!focusable.length) {
          event.preventDefault();
          container?.focus?.({ preventScroll: true });
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const activeElement = root.document?.activeElement;

        if (event.shiftKey) {
          if (activeElement === first || !container?.contains(activeElement)) {
            event.preventDefault();
            last.focus();
          }
          return;
        }

        if (activeElement === last || !container?.contains(activeElement)) {
          event.preventDefault();
          first.focus();
        }
      },

      setManagedInert(element, shouldBeInert) {
        if (!element) {
          return;
        }

        if (shouldBeInert) {
          if (!element.inert && !element.hasAttribute('inert')) {
            element.inert = true;
            element.setAttribute('inert', '');
            element.dataset.jftHeaderInert = 'true';
          }
          return;
        }

        if (element.dataset.jftHeaderInert === 'true') {
          element.inert = false;
          element.removeAttribute('inert');
          delete element.dataset.jftHeaderInert;
        }
      },

      setBodyBackgroundInert(shouldBeInert) {
        const body = root.document?.body;
        if (!body || !this.$root) {
          return;
        }

        let activeBranch = this.$root;
        let parent = activeBranch.parentElement;

        while (parent) {
          Array.from(parent.children).forEach((child) => {
            if (child !== activeBranch) {
              this.setManagedInert(child, shouldBeInert);
            }
          });

          if (parent === body) {
            break;
          }

          activeBranch = parent;
          parent = activeBranch.parentElement;
        }
      },

      releaseManagedInert() {
        root.document?.querySelectorAll?.('[data-jft-header-inert="true"]').forEach((element) => {
          element.inert = false;
          element.removeAttribute('inert');
          delete element.dataset.jftHeaderInert;
        });
      },

      syncOverlayState() {
        const mobileDialogOpen = this.drawerOpen && !this.isDesktop;
        const anyDialogOpen = mobileDialogOpen || this.searchDrawerOpen;
        const documentElement = root.document?.documentElement;

        documentElement?.classList.toggle('is-header-drawer-open', mobileDialogOpen);
        documentElement?.classList.toggle('is-search-drawer-open', this.searchDrawerOpen);
        this.setBodyBackgroundInert(anyDialogOpen);

        const headerContainer = this.$root?.querySelector('.site-header__container');
        const accountLink = this.$root?.querySelector('.site-header__account-link');
        const mobileBackgroundControls = this.$root?.querySelectorAll([
          '.site-header__logo',
          '.site-header__search-toggle',
          '.site-header__cart-link',
          '.site-header__contact-button',
        ].join(','));

        this.setManagedInert(headerContainer, this.searchDrawerOpen);
        this.setManagedInert(accountLink, mobileDialogOpen && !this.searchDrawerOpen);
        Array.from(mobileBackgroundControls || []).forEach((element) => {
          this.setManagedInert(element, mobileDialogOpen && !this.searchDrawerOpen);
        });
      },
    }));

    root.Alpine.data('searchDrawerScroll', () => ({
      scrollOverflowing: false,
      scrollAtEnd: true,
      scrollThumbHeight: 0,
      scrollThumbOffset: 0,
      scrollResizeObserver: null,
      dragging: false,
      swipeClosing: false,
      dragPointerId: null,
      dragStartY: 0,
      dragStartedAt: 0,
      dragOffsetY: 0,

      init() {
        this.$nextTick(() => {
          this.observeScrollArea();
          this.updateScrollState();
        });
      },

      destroy() {
        this.scrollResizeObserver?.disconnect();
        this.cancelDrag();
      },

      isMobileSwipe() {
        return root.matchMedia('(max-width: 749px)').matches;
      },

      startDrag(event) {
        const dragTarget = event.target.closest('.site-header__search-header, .site-header__search-handle');
        const interactiveTarget = event.target.closest('button, a, input, select, textarea');
        if (!this.isMobileSwipe() || !dragTarget || interactiveTarget || this.swipeClosing) return;

        this.dragging = true;
        this.dragPointerId = event.pointerId;
        this.dragStartY = event.clientY;
        this.dragStartedAt = Date.now();
        this.dragOffsetY = 0;
        this.$refs.panel?.setPointerCapture?.(event.pointerId);
      },

      moveDrag(event) {
        if (!this.dragging || event.pointerId !== this.dragPointerId) return;

        this.dragOffsetY = Math.max(0, event.clientY - this.dragStartY);
        if (this.dragOffsetY > 0) event.preventDefault();
      },

      async endDrag(event) {
        if (!this.dragging || event.pointerId !== this.dragPointerId) return;

        const elapsed = Math.max(Date.now() - this.dragStartedAt, 1);
        const velocity = this.dragOffsetY / elapsed;
        const panelHeight = this.$refs.panel?.offsetHeight || 0;
        const closeDistance = Math.min(120, panelHeight * 0.18);
        const shouldClose = this.dragOffsetY >= closeDistance
          || (this.dragOffsetY >= 24 && velocity >= 0.55);

        this.releaseDragPointer();
        this.dragging = false;

        if (!shouldClose) {
          this.dragOffsetY = 0;
          return;
        }

        this.swipeClosing = true;
        this.dragOffsetY = panelHeight + 8;
        const reducedMotion = root.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reducedMotion) {
          await new Promise((resolve) => root.setTimeout(resolve, 280));
        }

        this.$dispatch('search-drawer:close');
        root.setTimeout(() => {
          this.swipeClosing = false;
          this.dragOffsetY = 0;
        }, reducedMotion ? 0 : 220);
      },

      releaseDragPointer() {
        if (this.dragPointerId === null) return;

        try {
          this.$refs.panel?.releasePointerCapture?.(this.dragPointerId);
        } catch (_error) {
          // The pointer may already have been released by the browser.
        }
        this.dragPointerId = null;
      },

      cancelDrag() {
        this.releaseDragPointer();
        this.dragging = false;
        this.swipeClosing = false;
        this.dragOffsetY = 0;
      },

      panelDragStyle() {
        if (!this.dragging && !this.swipeClosing && this.dragOffsetY === 0) return '';
        return `transform: translateY(${this.dragOffsetY}px)`;
      },

      observeScrollArea() {
        this.scrollResizeObserver?.disconnect();
        this.scrollResizeObserver = null;

        if (!this.$refs.scrollArea || typeof root.ResizeObserver !== 'function') return;

        this.scrollResizeObserver = new root.ResizeObserver(() => this.updateScrollState());
        this.scrollResizeObserver.observe(this.$refs.scrollArea);
        Array.from(this.$refs.scrollArea.children).forEach((child) => {
          this.scrollResizeObserver.observe(child);
        });
      },

      updateScrollState() {
        const scrollArea = this.$refs.scrollArea;
        if (!scrollArea) return;

        this.scrollOverflowing = scrollArea.scrollHeight > scrollArea.clientHeight + 1;
        this.scrollAtEnd = !this.scrollOverflowing
          || scrollArea.scrollTop + scrollArea.clientHeight >= scrollArea.scrollHeight - 2;

        if (!this.scrollOverflowing) {
          this.scrollThumbHeight = scrollArea.clientHeight;
          this.scrollThumbOffset = 0;
          return;
        }

        const scrollRange = scrollArea.scrollHeight - scrollArea.clientHeight;
        this.scrollThumbHeight = Math.max(
          48,
          Math.round((scrollArea.clientHeight / scrollArea.scrollHeight) * scrollArea.clientHeight),
        );
        this.scrollThumbOffset = Math.round(
          (scrollArea.scrollTop / scrollRange) * (scrollArea.clientHeight - this.scrollThumbHeight),
        );
      },

      handleScroll() {
        this.updateScrollState();
      },
    }));
  }

  if (root.document && typeof root.document.addEventListener === 'function') {
    root.document.addEventListener('alpine:init', registerAlpine);
  }

  if (root.Alpine) {
    registerAlpine();
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      FOCUSABLE_SELECTOR,
      getFocusableElements,
      isVisibleFocusable,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
